import { memo, useEffect, useMemo, type JSX } from 'react';
import { Container } from 'Container';
import { type CurrentUserIguanaObject } from 'Users';
import { Chat, type OutgoingMessage, type PretendAiMessage } from 'TutorBotChat';
import { LoadingBoundary } from 'LoadingBoundary';
import clsx from 'clsx';
import { ReviewPreviousMaterialVoiceAgentRoom } from 'ReviewPreviousMaterialVoiceAgentRoom';
import { useEventLogger } from 'FrontRoyalAngular';
import { AI_TUTOR_BRAND_NAME } from '../constants';
import { useAiTutorStatusConfig } from '../hooks/useAiTutorStatusConfig';
import {
    type LessonPlayerBotAudioUiContext,
    type ReviewPreviousMaterialVoiceAgentAttributes,
    type LessonPlayerBotUiContext,
    type AiTutorClientContext,
    type LessonPlayerBotChatUiContext,
    type LessonPlayerBotContentInfo,
} from '../TutorBotAiTutorChat.types';
import { useToggleAudioOrText } from './useToggleAudioOrText';
import { isAudioUiContext, isChatUiContext } from '../utils/typeGuards';
import { getTutorBotChatClientContext, getVoiceAgentAttrs } from '../utils/tutorBotInputFactories';

type Props = {
    currentUser: CurrentUserIguanaObject;
    contentInfo: LessonPlayerBotContentInfo;
    uiContext: LessonPlayerBotChatUiContext;

    // the initialMessage will be passed through to Chat, so we borrow the type from there
    initialMessage?: OutgoingMessage | PretendAiMessage;
    className?: string;
    supportTab?: boolean;
};

type PresentationProps = Pick<Props, 'currentUser' | 'contentInfo' | 'initialMessage' | 'className' | 'supportTab'> & {
    uiContext: LessonPlayerBotUiContext;
    providedUiContext: LessonPlayerBotUiContext;
    toggleAudioOrTextConfig: ReturnType<typeof useToggleAudioOrText>['toggleAudioOrTextConfig'];
    autoStartVoiceConversation: boolean;
};

function Wrapper(props: Props) {
    return (
        <LoadingBoundary>
            <LessonPlayerBotComponent {...props} />
        </LoadingBoundary>
    );
}

// This is a bit brittle, but we are making room for the "Return to Screen", which
// only shows up in this position in mobile in the lesson player. 86px is:
// * 40px absolute positioning above the Return to Screen Button
// * minus 20px padding above the Chat element, which overlaps the 40px above the Return to Screen (=20px)
// * plus the 46px height of the Return to Screen Button (=66px)
// * plus 20px of spacing between the button and the welcome message (=86px)
const welcomeModalClassname = 'mt-[86px] sm:mt-0';

// See comment near HandlerProps
type ChatHandler = {
    component: 'Chat';
    uiContext: LessonPlayerBotChatUiContext;
    clientContext: AiTutorClientContext;
};

// See comment near HandlerProps
type ReviewPreviousMaterialAudioHandler = {
    component: 'ReviewPreviousMaterialVoiceAgentRoom';
    uiContext: LessonPlayerBotAudioUiContext;
    extraTokenAttributes: ReviewPreviousMaterialVoiceAgentAttributes;
};

// This bit with getHandler is a little confusing. There are some things (clientContext and streamId)
// that are not guaranteed to be non-null in all cases, but must be non-null given certain ui contexts.
// This function ensures that we have everything we need to render the given component.
type Handler = ChatHandler | ReviewPreviousMaterialAudioHandler;
function getHandler({
    uiContext,
    contentInfo,
}: Pick<PresentationProps, 'contentInfo'> & {
    uiContext: LessonPlayerBotUiContext;
}): Handler {
    if (isChatUiContext(uiContext)) {
        return {
            component: 'Chat',
            uiContext: uiContext as LessonPlayerBotChatUiContext,
            clientContext: getTutorBotChatClientContext(uiContext as LessonPlayerBotChatUiContext, contentInfo),
        };
    }

    if (isAudioUiContext(uiContext)) {
        return {
            component: 'ReviewPreviousMaterialVoiceAgentRoom',
            uiContext: uiContext as LessonPlayerBotAudioUiContext,
            extraTokenAttributes: getVoiceAgentAttrs({ uiContext, contentInfo }),
        };
    }

    throw new Error('Invalid modality');
}

function useLogLaunchEvent({
    uiContext,
    contentInfo,
}: {
    uiContext: LessonPlayerBotUiContext;
    contentInfo: LessonPlayerBotContentInfo;
}) {
    const EventLogger = useEventLogger();

    useEffect(() => {
        EventLogger.log('tutorbot:opened_ai_tutor', {
            label: uiContext,
            ui_context: uiContext,
            lesson_id: contentInfo.lessonId,
            stream_id: contentInfo.streamId,
        });
    }, [EventLogger, uiContext, contentInfo]);
}

export function LessonPlayerBotPresentation({
    currentUser,
    uiContext,
    providedUiContext,
    contentInfo,
    initialMessage,
    className,
    supportTab,
    autoStartVoiceConversation,
    toggleAudioOrTextConfig,
}: PresentationProps): JSX.Element {
    const botName = AI_TUTOR_BRAND_NAME;
    const tutorBotStatusConfig = useAiTutorStatusConfig({ botName });

    useLogLaunchEvent({ uiContext: providedUiContext, contentInfo });

    const handler = useMemo(() => getHandler({ uiContext, contentInfo }), [uiContext, contentInfo]);

    return (
        <Container className={clsx('h-full', className)}>
            {handler.component === 'Chat' && (
                <Chat
                    botName={botName}
                    currentUser={currentUser}
                    clientContext={handler.clientContext}
                    initialMessage={initialMessage}
                    tutorBotStatusConfig={tutorBotStatusConfig}
                    supportTab={supportTab}
                    welcomeModalClassname={welcomeModalClassname}
                    toggleAudioOrTextConfig={toggleAudioOrTextConfig}
                />
            )}
            {handler.component === 'ReviewPreviousMaterialVoiceAgentRoom' && (
                <ReviewPreviousMaterialVoiceAgentRoom
                    uiContext={handler.uiContext}
                    extraTokenAttributes={handler.extraTokenAttributes}
                    toggleAudioOrText={toggleAudioOrTextConfig!.toggleAudioOrText!}
                    autoStart={autoStartVoiceConversation}
                />
            )}
        </Container>
    );
}

function LessonPlayerBotComponent({ uiContext: providedUiContext, ...rest }: Props) {
    const { uiContext, toggleAudioOrTextConfig, autoStartVoiceConversation } = useToggleAudioOrText({
        providedUiContext,
    });

    return (
        <LessonPlayerBotPresentation
            uiContext={uiContext}
            providedUiContext={providedUiContext}
            toggleAudioOrTextConfig={toggleAudioOrTextConfig}
            autoStartVoiceConversation={autoStartVoiceConversation}
            {...rest}
        />
    );
}

export const LessonPlayerBot = memo(Wrapper) as typeof Wrapper;

export default LessonPlayerBot;
